import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { GlassCard } from '../components/GlassCard';
import { TrendingUp, TrendingDown, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';

function formatINR(n) {
  if (!n || n === 0) return '₹0';
  try { return '₹' + Math.round(n).toLocaleString('en-IN'); } catch { return '₹' + String(Math.round(n)); }
}

// Phase 25.I.3 — ERP-connected tenants: null = the feed doesn't send it.
function kpiMoney(n) {
  return n == null ? 'Not available from ERP' : formatINR(n);
}

const PERIODS = [
  { key: '30d', label: 'Last 30 Days' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: '60d', label: 'Last 60 Days' },
];

function periodRange(key) {
  const today = new Date();
  const ymd = (d) => d.toISOString().slice(0, 10);
  if (key === 'this_month') {
    return { from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), to: ymd(today) };
  }
  if (key === 'last_month') {
    return { from: ymd(new Date(today.getFullYear(), today.getMonth() - 1, 1)), to: ymd(new Date(today.getFullYear(), today.getMonth(), 0)) };
  }
  const days = key === '60d' ? 59 : 29;
  const from = new Date(today);
  from.setDate(from.getDate() - days);
  return { from: ymd(from), to: ymd(today) };
}

export default function Insights() {
  const { token } = useAuth();
  const [periodKey, setPeriodKey] = useState('30d');
  const [showPeriod, setShowPeriod] = useState(false);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [topCust, setTopCust] = useState([]);
  // Bakemill keeps bills in the ERP — show the feed's top products instead.
  const [erpTop, setErpTop] = useState(null);

  const range = useMemo(() => periodRange(periodKey), [periodKey]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [s, tc] = await Promise.all([
        api.insightsSummary(token, range.from, range.to),
        api.insightsTopCustomers(token, range.from, range.to),
      ]);
      setSummary(s);
      setTopCust(tc.items || []);
      setErpTop(tc.erp_top_products ? { items: tc.erp_top_products, date: tc.erp_business_date } : null);
    } catch (e) { /* swallow */ } finally { setLoading(false); }
  }, [token, range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="insights-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-white font-extrabold text-2xl tracking-tight">Insights</h1>
        <div className="relative">
          <button
            onClick={() => setShowPeriod(v => !v)}
            data-testid="period-chip"
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-brand-purple/15 text-brand-purple text-sm font-semibold hover:bg-brand-purple/20 transition-colors"
          >
            {PERIODS.find(p => p.key === periodKey)?.label}
            <ChevronDown size={14} />
          </button>
          {showPeriod && (
            <div className="absolute right-0 mt-2 w-48 rounded-xl bg-navy-800 border border-white/10 shadow-lg z-20 overflow-hidden">
              {PERIODS.map(p => (
                <button
                  key={p.key}
                  onClick={() => { setPeriodKey(p.key); setShowPeriod(false); }}
                  className={`w-full px-4 py-3 text-left text-sm hover:bg-white/5 transition-colors ${p.key === periodKey ? 'text-brand-purple font-bold' : 'text-white/70'}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-6 h-6 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" />
        </div>
      ) : summary ? (
        <div className="space-y-6">
          {/* KPI Row — all on one line */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-3 overflow-x-auto pb-1"
            data-testid="insights-kpi-row"
          >
            <KpiCard label={summary.current?.revenue_label || 'Revenue'} value={kpiMoney(summary.current?.total_revenue)} deltaPct={summary.deltas_pct?.revenue} color="text-brand-purple" />
            <KpiCard label="Expenses" value={kpiMoney(summary.current?.total_expenses)} deltaPct={summary.deltas_pct?.expenses} color="text-brand-purple-soft" inverse />
            <KpiCard label="Gross Profit" value={kpiMoney(summary.current?.gross_profit)} deltaPct={summary.deltas_pct?.gross_profit} color="text-emerald-400" />
            <KpiCard label="GST Collected" value={kpiMoney(summary.current?.gst_collected)} color="text-white" />
          </motion.div>

          {/* Top Customers */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <GlassCard dark className="p-5" testId="top-customers">
              <h3 className="text-white font-bold text-base mb-4">Top Customers</h3>
              {topCust.length === 0 && erpTop ? (
                <div className="space-y-3" data-testid="erp-top-products">
                  <p className="text-white/40 text-xs">
                    Top selling products · Source: Bakemill ERP{erpTop.date ? ` · ${erpTop.date}` : ''}
                  </p>
                  {erpTop.items.slice(0, 5).map((p, i) => (
                    <div key={`${p.name}-${i}`} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-brand-purple/15 flex items-center justify-center flex-shrink-0">
                        <span className="text-brand-purple text-xs font-bold">{i + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{p.name}</p>
                        <p className="text-white/40 text-xs">qty {p.qty_sold}</p>
                      </div>
                      <span className="text-white font-bold text-sm tabular-nums">{formatINR(p.revenue)}</span>
                    </div>
                  ))}
                </div>
              ) : topCust.length === 0 ? (
                <p className="text-white/40 text-sm">No sales in this period.</p>
              ) : (
                <div className="space-y-3">
                  {topCust.slice(0, 5).map((c, i) => (
                    <div key={c.customer_id} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-brand-purple/15 flex items-center justify-center flex-shrink-0">
                        <span className="text-brand-purple text-xs font-bold">{i + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{c.name}</p>
                        <p className="text-white/40 text-xs">{c.num_sales} sales</p>
                      </div>
                      <span className="text-white font-bold text-sm tabular-nums">{formatINR(c.revenue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
          </motion.div>
        </div>
      ) : (
        <p className="text-white/40 text-sm">No data available.</p>
      )}
    </div>
  );
}

function KpiCard({ label, value, deltaPct, color = 'text-white', inverse = false }) {
  const hasDelta = typeof deltaPct === 'number' && !Number.isNaN(deltaPct);
  const positive = hasDelta ? (inverse ? deltaPct < 0 : deltaPct > 0) : false;

  return (
    <GlassCard dark className="p-4 min-w-[160px] flex-shrink-0">
      <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">{label}</p>
      <p className={`font-extrabold text-xl mt-1 tabular-nums ${color}`}>{value}</p>
      {hasDelta && (
        <div className={`flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full w-fit ${positive ? 'bg-emerald-500/15' : 'bg-red-500/15'}`}>
          {positive ? <TrendingUp size={11} className="text-emerald-500" /> : <TrendingDown size={11} className="text-red-500" />}
          <span className={`text-[10px] font-bold ${positive ? 'text-emerald-500' : 'text-red-500'}`}>
            {deltaPct > 0 ? '+' : ''}{deltaPct}%
          </span>
        </div>
      )}
    </GlassCard>
  );
}
